import { elevation } from 'settings/tokens';
import { surfaceRadius } from 'settings/brandRadius';

/**
 * The surface shared by the certificate page's two STATE cards — the pending
 * transfer card (`TransferSummarySection`) and the lost/stolen callout
 * (`MissingStateCallout`).
 *
 * Both files already carried a comment saying the two "must stay identical", and
 * both then set their own background, border and radius. They had drifted: the
 * transfer card had no border, the callout had one. That is the second hand-roll,
 * so it becomes one source (working-conventions §36).
 *
 * ## Why it is now FILLED
 *
 * Measured on the live page, before this change:
 *
 * | | colour | vs. |
 * |---|---|---|
 * | page background | `#FFFFFF` | — |
 * | card background | `#FFFFFF` | **1.00 : 1** |
 *
 * The card and the page were the SAME COLOUR. The only thing separating them was
 * `elevation.card` — a 3px blur at 10% alpha — so the card had, in the literal
 * sense, no surface of its own. That is what "hard to read" was: not the type,
 * which measured 4.83:1 at its worst and 21:1 at its best, but the fact that
 * nothing told the eye where the block began or ended, so it did not group.
 *
 * `#F4F4F5` is the fill sibling of the border scale this app already uses
 * (`border.subtle` `#E4E4E7`, `border.strong` `#D4D4D8` — zinc 200/300; this is
 * zinc 100), so it introduces no new colour, only a step on a ramp already here.
 *
 * **It is not a status tint.** This component's own history records that a status
 * TINT and a left ACCENT BAR were each tried and rejected on sight. Both encoded
 * *which* state the card was in. This encodes only *that it is a card*, and it is
 * the identical neutral for lost, stolen, gift-in, gift-out and transfer-in.
 *
 * ## Honesty about the number
 *
 * 1.10:1 is what `#F4F4F5` on `#FFFFFF` measures, and quoting that as if it were
 * a pass would be nonsense — WCAG's ratio is calibrated for GLYPHS against their
 * immediate background, where the eye is resolving thin strokes. For two large
 * adjacent fields a ~10% luminance step is comfortably above threshold. The ratio
 * is reported because it was asked for, not because it is the right instrument;
 * the meaningful before/after is 1.00 (no boundary can exist) to 1.10 (one does).
 *
 * ## Fill AND shadow, but still no border
 *
 * The transfer card's own note argued border-plus-shadow was two containment
 * signals doing one job, and dropped the border. That still holds, and a fill is
 * the same argument again — so the border stays gone on BOTH cards and the pair
 * is fill + shadow. The shadow is kept rather than replaced because it is what
 * carries the meaning both files argue for at length: a pending transfer or a
 * missing report sits ON the certificate, it is not part of it.
 */
/*
 * ## The recipe is the CERTIFICATES CARD's, not a third invention
 *
 * The grey fill above was a fix for a real defect — the card was #FFFFFF on
 * #FFFFFF with `border-width: 0`, a 1.00:1 boundary that could not be seen — but
 * it solved it with a device this app uses nowhere else, so the card read as a
 * grey slab rather than as one of the app's cards.
 *
 * `CertificatesTeaser` already answers "how does a block stand out here", and the
 * answer is white + a 1px border + `elevation.card`. The border draws the edge;
 * the shadow lifts it off the page. Copying that makes the state card a member of
 * the same family instead of a one-off.
 *
 * This supersedes the "border-plus-shadow is two containment signals doing one
 * job" argument that dropped the border originally. That argument was made while
 * a FILL existed — fill plus border plus shadow is genuinely three. With the fill
 * gone it is two, which is the standard card recipe and what the list already
 * ships.
 *
 * ⚠️ The authored border COLOUR is ignored. `index.css`'s `#vera-shadow-root *`
 * sets `border-color` with ID specificity, so this renders at #E4E4E7 whatever is
 * written here — the same trap that made `MissingStateCallout`'s original border
 * invisible. That is fine and deliberate: #E4E4E7 is what every other border in
 * the app renders, including the list cards, so the state card matches by
 * accident of the same override. Do not "fix" it with `!important` — that would
 * make this the only border in the app that disagrees with the rest.
 */
export const stateCardSurfaceSx = {
  mb: 2,
  p: 2,
  // Never the numeric multiplier: MUI scales a numeric sx radius by the tenant's
  // Studio token at a factor nobody chose, so `2` rendered 16px on the default and
  // 20px on a brand that picked 10 (VERA-315). The fix for that was a literal 12px,
  // which then tracked the brand not at all (VRG-562). `surfaceRadius` is the third
  // thing: the tenant's token at a STATED step, 12px at the default so this still
  // matches the certificates card exactly, and moving with it above that.
  //
  // This one line is the highest-leverage site in the app — eight state cards on the
  // certificate page spread this object.
  borderRadius: surfaceRadius,
  backgroundColor: 'background.paper',
  border: '1px solid #E4E4E7',
  boxShadow: elevation.card,
} as const;

/**
 * Text-colour corrections for content the cards render through `@repo/shadcn-ui`'s
 * `Avatar`, which hardcodes `text-gray-500` / `text-gray-700` and takes no colour
 * prop. Scoped to the card so the shared package is untouched and no other surface
 * moves.
 *
 * `text-gray-500` (`#6B7280`) measured **4.83:1** on white — a pass, but only just,
 * at 14px — and the fill above costs it the last of that margin (**4.40:1**, a
 * fail). One step down the same ramp to `gray-600` `#4B5563` restores it to
 * **6.88:1** while staying visibly subordinate to the `#0A0A0A` name above it.
 *
 * ⚠️ `!important` IS REQUIRED, and the first attempt here without it silently did
 * nothing — measured on the live page, the timestamp still computed
 * `rgb(107, 114, 128)`. `tailwind.config.js` sets `important: '#vera-shadow-root'`,
 * so EVERY Tailwind utility in this app is emitted as
 * `#vera-shadow-root .text-gray-500 { … }` — ID specificity (1,1,0), which beats
 * an emotion descendant selector's (0,2,1).
 *
 * That is the same failure mode `settings/tokens.ts` documents for `border-color`,
 * but its note frames it as an `index.css` rule about borders. It is not: the
 * `important` selector strategy applies to every utility class in this app, so any
 * sx property that collides with a Tailwind class needs `!important`, not just
 * `borderColor`.
 *
 * The 1.5 line-height on the description follows §15 — descriptive body copy gets
 * air; Tailwind's `leading-5` at 14px is 1.43. Same `!important`, same reason
 * (`leading-5` is a utility too).
 */
export const stateCardTextSx = {
  '& p.text-gray-500': { color: '#4B5563 !important' },
  '& p.text-gray-700': { lineHeight: '1.5 !important' },
} as const;
